import { useEffect, useState } from 'react';

import { linksFromMarkdown, markdown } from '@trello/markdown';

import { useCanAddFileType } from 'app/src/components/BoardListView/useCanAddFileType';
import { useActionAttachmentUrlsFragment } from './ActionAttachmentUrlsFragment.generated';

export type PotentialAttachmentLink = {
  url: string;
  text?: string;
  title?: string;
};

interface UseAttachableLinksInCommentResult {
  potentialAttachmentLinks: PotentialAttachmentLink[];
}

interface UseAttachableLinksInCommentProps {
  commentText: string;
  isEligible: boolean;
  cardId: string;
}

export function useAttachableLinksInComment({
  commentText,
  isEligible,
  cardId,
}: UseAttachableLinksInCommentProps): UseAttachableLinksInCommentResult {
  const [canAttachLinks, setCanAttachLinks] = useState<boolean>(false);

  const { canAddFileType } = useCanAddFileType();

  const { data: attachmentsData } = useActionAttachmentUrlsFragment({
    from: { id: cardId },
    optimistic: true,
  });

  //All the checking in the legacy stack basically just boils down to
  // "can we attach links or not" despite the naming of the functions used
  //implying that they check the actual URLs and domains.
  useEffect(() => {
    const result = canAddFileType('link');
    setCanAttachLinks(result);
  }, [canAddFileType]);

  if (!isEligible || !canAttachLinks) {
    return {
      potentialAttachmentLinks: [],
    };
  }

  const linksFromComment = linksFromMarkdown(commentText, markdown.comments);
  const potentialAttachmentLinks = linksFromComment.reduce(
    (accumulator, potentialLink) => {
      if (
        attachmentsData?.attachments.find(
          (attachment) => attachment.url === potentialLink.url,
        )
      ) {
        return accumulator;
      }

      accumulator.push(potentialLink);
      return accumulator;
    },
    [] as PotentialAttachmentLink[],
  );
  return { potentialAttachmentLinks };
}
